import { Utils } from '@treezor/design-system';

import { IPaymentCard, TCardLimits, TLimitPeriod, TLimitType } from '@/features/cards/store/models/card.model';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import {
  IDashboardConfigSettingsCardsDesign,
  TDashboardConfigSettingsCardsDesignType,
} from '@/store/models/dashboard-config.model';

import { useProfileContext } from '@/features/context/ProfileContext';

export type TCardUnitCreationParams = Omit<IPaymentCard, 'cardId'> & {
  shouldConvertPhysical?: boolean;
  cardPrint?: number;
  cardId?: number;
};

interface IUseInitialCardProps {
  designType: TDashboardConfigSettingsCardsDesignType;
}

function containsStringOrArray(source: string, test: string | string[]): boolean {
  if (Array.isArray(test)) {
    return test.some((value) => source.indexOf(Utils.capitalize(value)) > 0);
  } else {
    return source.indexOf(Utils.capitalize(test)) > 0;
  }
}

type TLimitSource = TCardLimits | IPaymentCard | undefined | null;
type TObjectWithPrefix = { prefix: string; limits: TLimitSource };

export function hasPrefix(obj: TLimitSource | TObjectWithPrefix): obj is TObjectWithPrefix {
  return !!(obj as TObjectWithPrefix).prefix;
}

export function filterLimits(
  limits: TLimitSource,
  type?: TLimitType | TLimitType[],
  period?: TLimitPeriod | TLimitPeriod[],
): Partial<TCardLimits>;
export function filterLimits(
  limits: TObjectWithPrefix,
  type?: TLimitType | TLimitType[],
  period?: TLimitPeriod | TLimitPeriod[],
): Partial<TCardLimits>;
export function filterLimits(
  limits: TLimitSource | TObjectWithPrefix,
  type?: TLimitType | TLimitType[],
  period?: TLimitPeriod | TLimitPeriod[],
): Partial<TCardLimits> {
  let prefix = 'limit';
  if (hasPrefix(limits)) {
    prefix = limits.prefix;
    limits = limits.limits;
  }

  return Object.keys(limits || {}).reduce((prev, current) => {
    if (
      current.indexOf(prefix) > -1 &&
      (!type || containsStringOrArray(current, type)) &&
      (!period || containsStringOrArray(current, period))
    ) {
      return {
        [current]: limits[current],
        ...prev,
      };
    } else {
      return prev;
    }
  }, {});
}

export function getLimitValue(limits: TLimitSource, type: TLimitType, period: TLimitPeriod): number | undefined;
export function getLimitValue(limits: TObjectWithPrefix, type: TLimitType, period: TLimitPeriod): number | undefined;
export function getLimitValue(
  limits: TLimitSource | TObjectWithPrefix,
  type?: TLimitType,
  period?: TLimitPeriod,
): number | undefined {
  if (hasPrefix(limits)) {
    return Object.values(filterLimits(limits, type, period))?.[0];
  } else {
    return Object.values(filterLimits(limits, type, period))?.[0];
  }
}

export function useInitializeCard({ designType }: IUseInitialCardProps) {
  const { user } = useProfileContext();
  const config = useDashboardConfig();

  const designs: IDashboardConfigSettingsCardsDesign[] = (config?.settings?.cards?.designs || []).filter(
    (design) => design.type === designType,
  );

  const currentDesign = designs.length === 1 ? designs[0] : undefined;

  const initialCard: TCardUnitCreationParams = {
    userId: user ? user.userId : undefined,
    walletId: undefined,
    cardPrint: currentDesign?.value,
    permsGroup: 'TRZ-CU-001',
    sendToParent: 0,
  };

  return { initialCard, designs, currentDesign };
}
